import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@project/hooks/useAuth";

import { PageShell } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import {
  HandHeart,
  Receipt,
  LogOut,
  Loader2,
  User as UserIcon,
  IndianRupee,
  Calendar,
  Save,
  Edit,
  Sparkles,
  MapPin,
  ClipboardList,
  Users,
  FileText,
  Download,
  DownloadIcon,
  Eye,
  Mail,
  Phone,
  Globe,
  Utensils,
  BookOpen,
  CreditCard,
  Award,
  ShoppingBag,
  Package,
  HeartPulse,
  Leaf,
  HandCoins,
} from "lucide-react";
import { toast } from "sonner";
import { generateReceiptPdf, downloadProductOrderReceiptPdf } from "@project/lib/receipt";
import type { ProductOrder, ProductOrderItem } from "@project/types/gruh-udhyog";
import { downloadEntryPassPDF } from "@project/lib/entry-pass-pdf";
import { formatDate } from "@project/lib/date-format";
import { useI18n } from "@project/lib/use-i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";

import {
  linkAndGetDonor,
  getDonorOverview,
  updateDonorProfile,
} from "@project/lib/donor-portal.functions";
import { userDetailsService } from "@project/services/user/user-details.service";
import { eventRegistrationService } from "@project/services/admin/event-registration.service";
import { eventService } from "@project/services/admin/event.service";
import { get } from "@project/services/shared/api-client";

const inr = (n: number) => `₹${Number(n ?? 0).toLocaleString("en-IN")}`;

const safeStr = (val: any, fallback = "—"): string => {
  if (val === null || val === undefined || val === "") return fallback;
  if (typeof val === "object") {
    if (typeof val.full_address === "string" && val.full_address.trim()) return val.full_address;
    if (typeof val.address === "string" && val.address.trim()) return val.address;
    if (typeof val.name === "string" && val.name.trim()) return val.name;
    return fallback;
  }
  return String(val);
};

const getDonationAddress = (d: any) => {
  if (!d) return { address: "—", city: "—", state: "—", pincode: "—", country: "India" };
  const donorObj = typeof d.donor === "object" && d.donor !== null ? d.donor : {};
  const addrObj = typeof d.address === "object" && d.address !== null ? d.address : {};

  const address =
    typeof d.address === "string" && d.address.trim()
      ? d.address
      : addrObj.full_address ||
        addrObj.address ||
        addrObj.address_line_1 ||
        (typeof donorObj.address === "string" ? donorObj.address : "") ||
        (typeof d.full_address === "string" ? d.full_address : "") ||
        "—";

  const city =
    (typeof addrObj.city === "string" ? addrObj.city : "") ||
    (typeof d.city === "string" ? d.city : "") ||
    (typeof donorObj.city === "string" ? donorObj.city : "") ||
    "—";

  const state =
    (typeof addrObj.state === "string" ? addrObj.state : "") ||
    (typeof d.state === "string" ? d.state : "") ||
    (typeof donorObj.state === "string" ? donorObj.state : "") ||
    "—";

  const pincode =
    (typeof addrObj.pincode === "string" ? addrObj.pincode : "") ||
    (typeof d.pincode === "string" ? d.pincode : "") ||
    (typeof donorObj.pincode === "string" ? donorObj.pincode : "") ||
    "—";

  const country =
    (typeof addrObj.country === "string" ? addrObj.country : "") ||
    (typeof d.country === "string" ? d.country : "") ||
    (typeof donorObj.country === "string" ? donorObj.country : "") ||
    "India";

  return { address, city, state, pincode, country };
};

const formatDisplayDate = (val?: any) => {
  if (!val) return "—";
  try {
    const formatted = formatDate(val);
    if (formatted) return formatted;
    const dt = new Date(val);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    }
    return "—";
  } catch {
    return "—";
  }
};

export default function AccountPage() {
  const { t } = useI18n();
  const nav = useNavigate();

  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [donor, setDonor] = useState<any | null>(null);
  const [userDetails, setUserDetails] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState<any | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [stats, setStats] = useState<any>({
    lifetime_total: 0,
    lifetime_count: 0,
    fy_total: 0,
  });
  const [donations, setDonations] = useState<any[]>([]);
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedProductOrder, setSelectedProductOrder] = useState<ProductOrder | null>(null);
  const [isProductOrderDetailsOpen, setIsProductOrderDetailsOpen] = useState(false);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [eventsMap, setEventsMap] = useState<Record<number, any>>({});

  const {
    isAuthenticated,
    user: authUser,
    logout: authLogout,
    updateSessionUser,
  } = useAuth();

  const completionPercentage = useMemo(() => {
    let score = 0;
    if (authUser?.first_name) {
      score += 10;
    }
    if (authUser?.last_name) {
      score += 10;
    }
    if (authUser?.phone_number) {
      score += 10;
    }
    if (authUser?.email) {
      score += 10;
    }
    if (authUser?.gender && authUser.gender !== "—" && authUser.gender !== "") {
      score += 10;
    }
    if (
      authUser?.date_of_birth &&
      authUser.date_of_birth !== "—" &&
      authUser.date_of_birth !== ""
    ) {
      score += 10;
    }

    if (userDetails?.address_line_1) {
      score += 10;
    }
    if (
      userDetails?.city &&
      userDetails.city !== "—" &&
      userDetails.city !== ""
    ) {
      score += 10;
    }
    if (
      userDetails?.state &&
      userDetails.state !== "—" &&
      userDetails.state !== ""
    ) {
      score += 10;
    }
    if (
      userDetails?.pincode &&
      userDetails.pincode !== "—" &&
      userDetails.pincode !== ""
    ) {
      score += 10;
    }

    return Math.min(100, Math.max(0, score));
  }, [authUser, userDetails]);

  const fetchDonationsData = async () => {
    try {
      const res = await get<any>("/jivandhara/donations/my-history/");
      if (res && res.success && Array.isArray(res.data)) {
        const mappedDonations = res.data.map((d: any) => {
          const donorObj = typeof d.donor === "object" && d.donor !== null ? d.donor : {};
          const addr = getDonationAddress(d);
          return {
            ...d,
            id: d.id,
            donation_date: d.donation_date || d.created_at || d.date || new Date().toISOString(),
            receipt_no: d.receipt_no || `REC-JVD-${100000 + d.id}`,
            purpose: d.sub_category || d.bhojan_name || d.scheme_name || d.donation_type || d.category || d.type || "—",
            payment_mode: d.payment_mode || d.mode || "—",
            amount: Number(d.amount ?? 0),
            payment_status: d.payment_status || d.status || "success",
            transaction_id: d.transaction_id || `TXN-JVD-${d.id}`,
            requires_80g: d.requires_80g !== false,
            donor: {
              full_name: donorObj.full_name || d.donor_name || d.full_name || "",
              mobile: donorObj.mobile || donorObj.phone || d.phone || d.mobile || "",
              email: donorObj.email || d.email || "",
              address: addr.address,
              city: addr.city,
              state: addr.state,
              pincode: addr.pincode,
              pan: donorObj.pan || d.pan || "",
              country: addr.country,
            },
          };
        });

        setDonations(mappedDonations);

        // Calculate stats based on success payments
        const successDons = mappedDonations.filter((d: any) => d.payment_status === "success");
        const lifetime_total = successDons.reduce((sum: number, d: any) => sum + d.amount, 0);
        setStats({
          lifetime_total,
          lifetime_count: successDons.length,
          fy_total: lifetime_total,
          last_donation_at: successDons[0]?.donation_date ?? null,
        });
      }
    } catch (err) {
      console.error("Failed to load Jivandhara donations history:", err);
    }
  };



  const fetchOrdersData = async () => {
    setOrdersLoading(true);
    try {
      const res = await get<any>("/jivandhara/product-orders/");
      let list: ProductOrder[] = [];
      if (res && res.success && Array.isArray(res.data)) {
        list = res.data;
      } else if (Array.isArray(res)) {
        list = res;
      } else if (res && Array.isArray(res.results)) {
        list = res.results;
      }
      setOrders(list);
    } catch (err) {
      console.error("Failed to load product orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleViewProductOrderDetails = async (order: ProductOrder) => {
    try {
      const res = await get<any>(`/jivandhara/product-orders/${order.id}/`);
      if (res && res.success && res.data) {
        setSelectedProductOrder(res.data);
      } else if (res && res.id) {
        setSelectedProductOrder(res);
      } else {
        setSelectedProductOrder(order);
      }
    } catch (err) {
      console.error("Failed to fetch product order details:", err);
      setSelectedProductOrder(order);
    }
    setIsProductOrderDetailsOpen(true);
  };

  const fetchEventsData = async () => {
    try {
      const regRes = await eventRegistrationService.listPage({
        page_size: 200,
      });
      setRegistrations(regRes.data ?? []);
    } catch (err) {
      console.error("Failed to load registrations:", err);
    }

    try {
      const evtRes = await eventService.listPage({ page_size: 200 });
      const map: Record<number, any> = {};
      if (evtRes.data) {
        evtRes.data.forEach((e: any) => {
          map[e.id] = e;
        });
      }
      setEventsMap(map);
    } catch (err) {
      console.error("Failed to load events list:", err);
    }
  };

  const fetchProfileData = async () => {
    try {
      const details = await userDetailsService.get();
      setUserDetails(details ?? null);
    } catch (err) {
      console.error("Failed to load user details:", err);
    }
  };

  const refresh = async () => {
    try {
      const overview = await getDonorOverview();
      setDonor(overview.donor);
    } catch { }

    if (activeTab === "donations") {
      await fetchDonationsData();
    } else if (activeTab === "orders" || activeTab === "recurring") {
      await fetchOrdersData();
    } else if (activeTab === "events") {
      await fetchEventsData();
    } else if (activeTab === "profile") {
      await fetchProfileData();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      nav({ to: "/login", search: { redirectTo: "/account" } });
      return;
    }

    setEmail(authUser?.email ?? authUser?.phone_number ?? null);
    setName(
      authUser?.first_name
        ? `${authUser.first_name} ${authUser.last_name ?? ""}`.trim()
        : (authUser?.username ?? t("account.account.hero.donorFallback"))
    );

    (async () => {
      try {
        await linkAndGetDonor();
        const overview = await getDonorOverview();
        setDonor(overview.donor);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, authUser]);

  useEffect(() => {
    if (loading || !isAuthenticated) return;

    if (activeTab === "donations") {
      fetchDonationsData();
    } else if (activeTab === "orders" || activeTab === "recurring") {
      fetchOrdersData();
    } else if (activeTab === "events") {
      fetchEventsData();
    } else if (activeTab === "profile") {
      fetchProfileData();
    }
  }, [activeTab, loading, isAuthenticated]);

  useEffect(() => {
    if (!loading) {
      setActiveTab(donor ? "donations" : "profile");
    }
  }, [loading, donor]);

  const signOut = async () => {
    authLogout();
    toast.success(t("account.account.toast.signedOut"));
    nav({ to: "/" });
  };

  const downloadOne = (d: any) => {
    let title = "Donation Receipt";
    let donationType = "General";
    let donationScheme = d.purpose || d.category || "—";
    let preferredTithi = d.preferred_tithi || "";
    let occasion = d.occasion || "";

    if (d.type === "healthcare") {
      title = "Healthcare Donation Receipt";
      donationType = "Healthcare";
      donationScheme = `${d.category || d.donation_type || "Healthcare"} - ${d.sub_category || ""}`;
    } else if (d.type === "bhojan") {
      title = "Bhojanshala Donation Receipt";
      donationType = "Bhojanshala";
      donationScheme = d.scheme_name || d.bhojan_name || "Bhojanshala Donation";
    } else if (d.type === "gaushala") {
      title = "Gaushala Donation Receipt";
      donationType = "Gaushala";
    }

    const addr = getDonationAddress(d);
    const donorAddress = [addr.address, addr.city, addr.state, addr.pincode]
      .filter(p => p && p !== "—")
      .join(", ");

    generateReceiptPdf({
      title,
      receiptNo: safeStr(d.receipt_no || `REC-JVD-${100000 + d.id}`),
      date: d.donation_date || d.created_at || "",
      donationScheme: safeStr(donationScheme),
      donorName: safeStr(d.donor?.full_name || d.donor_name, "Anonymous Donor"),
      donorMobile: safeStr(d.donor?.mobile || d.phone, ""),
      donorEmail: safeStr(d.donor?.email || d.email, ""),
      donorAddress: donorAddress || safeStr(d.full_address, ""),
      donorPan: safeStr(d.donor?.pan || d.pan, ""),
      amount: Number(d.amount ?? 0),
      donationType,
      paymentMode: safeStr(d.payment_mode || d.mode, "Offline"),
      cashOrChequeNo: safeStr(d.transaction_id || d.payments?.[0]?.razorpay_payment_id, ""),
      preferredTithi,
      occasion,
    });
  };

  // Removed unused yearly statement downloads

  return (
    <PageShell>
      <section className="from-primary/5 to-gold/10 border-b bg-gradient-to-br via-background">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
                <UserIcon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("account.account.hero.myAccount")}
                </p>
                <h1 className="text-2xl font-bold sm:text-3xl">
                  {donor?.full_name ?? name}
                </h1>
                <p className="text-sm text-muted-foreground">{email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button asChild className="rounded-full">
                <Link to="/donate">
                  <HandHeart className="mr-2 h-4 w-4" />
                  {t("account.account.hero.donateAgain")}
                </Link>
              </Button>
              <Button
                onClick={signOut}
                variant="outline"
                className="rounded-full"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("account.account.hero.signOut")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="grid min-h-[40vh] place-items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gold" />
        </div>
      ) : (
        <>
          {donor && (
            <section className="mx-auto grid max-w-6xl gap-4 px-4 py-8 sm:grid-cols-4">
              <StatCard
                icon={<IndianRupee className="h-4 w-4" />}
                label={t("account.account.stats.lifetimeTotal")}
                value={inr(Number(stats.lifetime_total ?? 0))}
              />
              <StatCard
                icon={<Receipt className="h-4 w-4" />}
                label={t("account.account.stats.donations")}
                value={String(stats.lifetime_count ?? 0)}
              />
              <StatCard
                icon={<Calendar className="h-4 w-4" />}
                label={t("account.account.stats.thisFy")}
                value={inr(Number(stats.fy_total ?? 0))}
              />
              <StatCard
                icon={<ShoppingBag className="h-4 w-4" />}
                label="Product Orders"
                value={String(orders.length)}
              />
            </section>
          )}

          <section className="mx-auto max-w-6xl px-4 pb-16 pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:grid-cols-4">
                <TabsTrigger value="donations">
                  {t("account.account.tabs.donations")}
                </TabsTrigger>
                <TabsTrigger value="orders" className="flex items-center gap-1.5">
                  <ShoppingBag className="h-4 w-4" />
                  My Orders
                </TabsTrigger>
                <TabsTrigger value="events">Event Registrations</TabsTrigger>
                <TabsTrigger value="profile">
                  {t("account.account.tabs.profile")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="donations" className="mt-4">
                <DonationsTable
                  rows={donations}
                  onDownload={downloadOne}
                  onViewDetails={(d) => {
                    setSelectedDonation(d);
                    setIsDetailsOpen(true);
                  }}
                />
              </TabsContent>

              <TabsContent value="orders" className="mt-4">
                <ProductOrdersList
                  rows={orders}
                  loading={ordersLoading}
                  onViewDetails={handleViewProductOrderDetails}
                  onDownloadReceipt={downloadProductOrderReceiptPdf}
                />
              </TabsContent>

              <TabsContent value="events" className="mt-4">
                <div className="rounded-2xl border bg-card p-6">
                  <h3 className="text-lg font-semibold">
                    My Registered Events
                  </h3>
                  <p className="mb-6 mt-1 text-sm text-muted-foreground">
                    A list of upcoming and past community events you have
                    registered for.
                  </p>

                  {!registrations.length ? (
                    <div className="rounded-2xl border border-dashed p-10 text-center">
                      <ClipboardList className="text-muted-foreground/60 mx-auto h-12 w-12" />
                      <h3 className="mt-4 text-lg font-semibold">
                        No event registrations yet
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground">
                        You haven't registered for any community events. Check
                        out our upcoming events!
                      </p>
                      <Button asChild className="mt-4" variant="outline">
                        <Link to="/events">Browse Events</Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {registrations.map(reg => {
                        const evt = eventsMap[reg.event];
                        return (
                          <div
                            key={reg.id}
                            className="shadow-xs relative overflow-hidden rounded-2xl border bg-card p-6 transition-shadow hover:shadow-md"
                          >
                            <div className="absolute left-0 top-0 h-full w-2 bg-[#A66E19]" />
                            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                              <div className="space-y-2 pl-2">
                                <span className="inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                                  Ticket #{reg.id}
                                </span>
                                <h4 className="font-serif text-lg font-bold text-foreground">
                                  {evt?.event_title ?? `Event #${reg.event}`}
                                </h4>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {evt ? formatDate(evt.event_date ?? evt.start_date ?? evt.date) : "—"}
                                  </span>
                                  {evt?.venue && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="h-3.5 w-3.5" />
                                      {evt.venue}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3.5 w-3.5" />
                                    {reg.total_attendees}{" "}
                                    {reg.total_attendees > 1
                                      ? "Attendees"
                                      : "Attendee"}
                                  </span>
                                </div>
                                {reg.notes && (
                                  <div className="bg-muted/50 mt-2 flex items-start gap-1 rounded-lg p-2 text-xs text-muted-foreground">
                                    <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>
                                      <strong>Notes:</strong> {reg.notes}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
                                <span className="text-xs text-muted-foreground">
                                  Registered on:{" "}
                                  {new Date(reg.created_at).toLocaleDateString(
                                    "en-IN"
                                  )}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadEntryPassPDF({
                                      bookingRef: reg.booking_ref || `JVD-EVT-${100000 + reg.id}`,
                                      eventTitle: evt?.event_title ?? `Event #${reg.event}`,
                                      eventDate: evt?.event_date ?? evt?.start_date ?? "",
                                      startTime: evt?.start_time,
                                      venue: evt?.venue || "Jivandhara Campus, Palitana",
                                      fullName: reg.full_name || donor?.full_name || name || "Guest",
                                      guests: reg.total_attendees || 1,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 hover:underline"
                                >
                                  <Download className="h-3 w-3 text-amber-600" />
                                  Download Entry Pass
                                </button>
                                {reg.image && (
                                  <a
                                    href={reg.image}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center text-xs text-[#A66E19] hover:underline"
                                  >
                                    View Attached Pass/Image
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="profile" className="mt-4">
                <div className="space-y-6">
                  {/* Header section with Edit Button */}
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
                    <div>
                      <h3 className="text-xl font-bold text-primary">
                        Profile Information
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        View and manage your personal info, contact numbers, and
                        primary addresses.
                      </p>
                    </div>
                    <Button
                      onClick={() => setIsEditOpen(true)}
                      className="hover:bg-primary/95 rounded-full bg-primary shadow-sm"
                    >
                      <Edit className="mr-2 h-4 w-4" /> Edit Profile
                    </Button>
                  </div>

                  {/* Notice banner if no donor profile */}
                  {/* {!donor && (
                    <div className="rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-700">
                      <p className="font-bold">No Donor Profile Linked Yet</p>
                      <p className="mt-1">
                        Make your first donation and a tax receipt donor profile will be automatically linked to your account.
                      </p>
                    </div>
                  )} */}

                  {/* Profile Completion Progress Card */}
                  <div className="via-primary/5 relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50/50 to-amber-50/30 p-6 shadow-sm">
                    {/* Background gold decorative blur */}
                    <div className="bg-gold/10 absolute right-0 top-0 -mr-6 -mt-6 h-24 w-24 animate-pulse rounded-full blur-xl" />

                    <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${completionPercentage === 100
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-amber-200 bg-amber-50 text-amber-700"
                              }`}
                          >
                            <Sparkles className="duration-3000 h-3 w-3 animate-spin" />
                            {completionPercentage === 100
                              ? "Verified Profile"
                              : "Profile Strength"}
                          </span>
                        </div>
                        <h4 className="font-serif text-xl font-bold leading-tight text-foreground">
                          {completionPercentage === 100
                            ? "Your profile is 100% complete!"
                            : "Complete your profile details to place orders"}
                        </h4>
                        <p className="max-w-md text-xs text-muted-foreground">
                          {completionPercentage === 100
                            ? "Your profile details are fully complete. Thank you!"
                            : "Please fill in your primary address, city, state, and pincode in your profile details to unlock ordering snacks."}
                        </p>
                      </div>

                      <div className="flex min-w-[200px] flex-col gap-2 sm:items-end">
                        <div className="flex w-full items-baseline justify-between gap-2 sm:justify-end">
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Completion:
                          </span>
                          <span
                            className={`text-3xl font-black ${completionPercentage === 100
                              ? "text-emerald-600"
                              : "text-primary"
                              }`}
                          >
                            {completionPercentage}%
                          </span>
                        </div>
                        <div className="border-border/40 relative h-3.5 w-full overflow-hidden rounded-full border bg-secondary">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ease-out ${completionPercentage === 100
                              ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                              : "bg-gradient-to-r from-primary to-amber-500"
                              }`}
                            style={{ width: `${completionPercentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Profile Cards Grid */}
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Card 1: Personal Details */}
                    <div className="shadow-xs rounded-2xl border bg-card p-6">
                      <h4 className="mb-5 flex items-center gap-2 border-b border-dashed pb-2 font-serif text-lg font-bold text-[#A66E19]">
                        <UserIcon className="h-5 w-5 shrink-0 text-amber-500" />
                        Personal Information
                      </h4>
                      <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-2">
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Full Name
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.full_name ?? donor?.full_name ?? name}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            First Name
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.first_name ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Last Name
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.last_name ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Username
                          </span>
                          <span className="mt-1 block break-all font-medium text-foreground">
                            {authUser?.username ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Mobile Number
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.phone_number ?? donor?.mobile ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Email Address
                          </span>
                          <span className="mt-1 block break-all font-medium text-foreground">
                            {authUser?.email ?? donor?.email ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Gender
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.gender ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Date of Birth
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {authUser?.date_of_birth ??
                              donor?.date_of_birth ??
                              "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            PAN Number
                          </span>
                          <span className="mt-1 block font-mono font-medium uppercase text-foreground">
                            {donor?.pan ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Country
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {donor?.country ?? "India"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card 2: User Address Details */}
                    <div className="shadow-xs rounded-2xl border bg-card p-6">
                      <h4 className="mb-5 flex items-center gap-2 border-b border-dashed pb-2 font-serif text-lg font-bold text-[#A66E19]">
                        <MapPin className="h-5 w-5 shrink-0 text-amber-500" />
                        User Details Address
                      </h4>
                      <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Address Line 1
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.address_line_1 ?? "—"}
                          </span>
                        </div>
                        <div className="sm:col-span-2">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Address Line 2
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.address_line_2 ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Landmark
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.landmark ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Alternate Phone
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.alternate_phone_number ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            City
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.city ?? "—"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            State
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.state ?? "—"}
                          </span>
                        </div>
                        <div className="sm:col-span-2">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Pincode
                          </span>
                          <span className="mt-1 block font-medium text-foreground">
                            {userDetails?.pincode ?? "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card 3: Donor Address Details (Conditional) */}
                    {donor && (
                      <div className="shadow-xs rounded-2xl border bg-card p-6 md:col-span-2">
                        <h4 className="mb-5 flex items-center gap-2 border-b border-dashed pb-2 font-serif text-lg font-bold text-[#A66E19]">
                          <Receipt className="h-5 w-5 shrink-0 text-amber-500" />
                          Billing Donor Address (For Tax Receipt & 80G Benefit)
                        </h4>
                        <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Billing Address
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.address ?? "—"}
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              City
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.city ?? "—"}
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              State
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.state ?? "—"}
                            </span>
                          </div>
                          <div>
                            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Pincode
                            </span>
                            <span className="mt-1 block font-medium text-foreground">
                              {donor?.pincode ?? "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Edit Profile Modal Dialog */}
              <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white p-6 sm:rounded-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-primary">
                      Edit Profile Details
                    </DialogTitle>
                  </DialogHeader>
                  <ProfileForm
                    donor={donor}
                    userDetails={userDetails}
                    onSave={async (
                      donorPayload,
                      userDetailsPayload,
                      personalPayload
                    ) => {
                      try {
                        if (donor) {
                          await updateDonorProfile({ data: donorPayload });
                        }
                        await userDetailsService.save(userDetailsPayload);
                        if (authUser?.id) {
                          updateSessionUser({
                            first_name: personalPayload.first_name,
                            last_name: personalPayload.last_name,
                            full_name: personalPayload.full_name,
                            phone_number: personalPayload.phone_number,
                            gender: personalPayload.gender,
                            date_of_birth: personalPayload.date_of_birth,
                          });
                        }
                        toast.success(
                          t("account.account.toast.profileUpdated")
                        );
                        setIsEditOpen(false);
                        await refresh();
                      } catch (e: any) {
                        toast.error(
                          e?.message ?? t("account.account.toast.failed")
                        );
                      }
                    }}
                    onCancel={() => setIsEditOpen(false)}
                  />
                </DialogContent>
              </Dialog>

              {/* Donation Details Modal Dialog */}
              <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
                <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-[#FBF9F6] p-6 sm:rounded-2xl border-none">
                  {selectedDonation && (() => {
                    const addr = getDonationAddress(selectedDonation);
                    const typeStr = String(
                      selectedDonation.donation_type ||
                      selectedDonation.type ||
                      selectedDonation.category ||
                      ""
                    ).toLowerCase();

                    let modalTitle = "Donation Details";
                    if (typeStr.includes("bhojan") || typeStr.includes("food") || typeStr.includes("meal") || typeStr.includes("tiffin")) {
                      modalTitle = "Bhojan Donation Details";
                    } else if (typeStr.includes("health") || typeStr.includes("medical") || typeStr.includes("care")) {
                      modalTitle = "Healthcare Donation Details";
                    } else if (typeStr.includes("gaushala") || typeStr.includes("gau") || typeStr.includes("cow")) {
                      modalTitle = "Gaushala Donation Details";
                    } else if (typeStr.includes("education") || typeStr.includes("vidya") || typeStr.includes("book")) {
                      modalTitle = "Education Donation Details";
                    } else if (selectedDonation.category) {
                      modalTitle = `${selectedDonation.category} Donation Details`;
                    }

                    return (
                      <div className="space-y-6">
                        {/* Header Block */}
                        <DialogHeader className="border-b pb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="bg-primary/10 text-primary grid h-10 w-10 place-items-center rounded-xl">
                                <HandCoins className="h-5 w-5" />
                              </span>
                              <DialogTitle className="font-serif text-2xl font-bold text-foreground">
                                {modalTitle}
                              </DialogTitle>
                            </div>
                            <div>
                              <span className="bg-emerald-600 text-white text-xs px-3 py-1 font-bold rounded-full capitalize">
                                {safeStr(selectedDonation.payment_status || selectedDonation.status, "Success")}
                              </span>
                            </div>
                          </div>
                        </DialogHeader>

                        {/* Top Metadata Grid */}
                        <div className="grid grid-cols-2 gap-y-4 gap-x-8 sm:grid-cols-4 py-2 border-b">
                          <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">
                              Transaction Amount
                            </span>
                            <span className="text-sm font-bold text-foreground mt-1 block">
                              {inr(Number(selectedDonation.amount ?? 0))}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">
                              Date
                            </span>
                            <span className="text-sm font-semibold text-foreground mt-1 block">
                              {formatDisplayDate(selectedDonation.created_at || selectedDonation.donation_date)}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">
                              Package / Scheme
                            </span>
                            <span className="text-sm font-semibold text-foreground mt-1 block truncate capitalize">
                              {safeStr(selectedDonation.sub_category || selectedDonation.bhojan_name || selectedDonation.scheme_name || selectedDonation.donation_type || selectedDonation.category)}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">
                              Category
                            </span>
                            <span className="text-sm font-semibold text-foreground mt-1 block truncate capitalize">
                              {safeStr(selectedDonation.category || selectedDonation.donation_type || selectedDonation.type)}
                            </span>
                          </div>
                        </div>

                        {/* Middle Cards: Donor info & Address */}
                        <div className="grid gap-6 md:grid-cols-2">
                          {/* Card 1: Donor information */}
                          <div className="rounded-2xl border bg-white p-6 shadow-sm">
                            <h3 className="mb-6 flex items-center gap-2 font-serif text-lg font-bold text-foreground">
                              <UserIcon className="h-5 w-5 text-primary shrink-0" />
                              Donor information
                            </h3>
                            <div className="space-y-4 text-xs">
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                  <UserIcon className="h-3.5 w-3.5 text-primary" /> DONOR NAME
                                </span>
                                <span className="font-semibold text-foreground mt-1 block ml-5">
                                  {safeStr(selectedDonation.donor?.full_name || selectedDonation.donor_name)}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                  <UserIcon className="h-3.5 w-3.5 text-primary" /> DISPLAY NAME
                                </span>
                                <span className="font-semibold text-foreground mt-1 block ml-5">
                                  {safeStr(selectedDonation.donor?.full_name || selectedDonation.donor_name || name)}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                  <Mail className="h-3.5 w-3.5 text-primary" /> EMAIL ADDRESS
                                </span>
                                <span className="font-semibold text-foreground mt-1 block ml-5 break-all">
                                  {safeStr(selectedDonation.donor?.email || selectedDonation.email)}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                  <Phone className="h-3.5 w-3.5 text-primary" /> PHONE NUMBER
                                </span>
                                <span className="font-semibold text-foreground mt-1 block ml-5">
                                  {safeStr(selectedDonation.donor?.mobile || selectedDonation.phone)}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                  <FileText className="h-3.5 w-3.5 text-primary" /> RECEIPT NO
                                </span>
                                <span className="font-mono font-bold text-foreground mt-1 block ml-5">
                                  {safeStr(selectedDonation.receipt_no)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Card 2: Address */}
                          <div className="rounded-2xl border bg-white p-6 shadow-sm">
                            <h3 className="mb-6 flex items-center gap-2 font-serif text-lg font-bold text-foreground">
                              <MapPin className="h-5 w-5 text-primary shrink-0" />
                              Address
                            </h3>
                            <div className="space-y-4 text-xs">
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                  <MapPin className="h-3.5 w-3.5 text-primary" /> FULL ADDRESS
                                </span>
                                <span className="font-semibold text-foreground mt-1 block ml-5">
                                  {safeStr(addr.address)}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                    <MapPin className="h-3.5 w-3.5 text-primary" /> CITY
                                  </span>
                                  <span className="font-semibold text-foreground mt-1 block ml-5">
                                    {safeStr(addr.city)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                    <MapPin className="h-3.5 w-3.5 text-primary" /> STATE
                                  </span>
                                  <span className="font-semibold text-foreground mt-1 block ml-5">
                                    {safeStr(addr.state)}
                                  </span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                    <MapPin className="h-3.5 w-3.5 text-primary" /> PINCODE
                                  </span>
                                  <span className="font-semibold text-foreground mt-1 block ml-5">
                                    {safeStr(addr.pincode)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                    <Globe className="h-3.5 w-3.5 text-primary" /> COUNTRY
                                  </span>
                                  <span className="font-semibold text-foreground mt-1 block ml-5">
                                    {safeStr(addr.country, "India")}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bottom sponsorship details card */}
                        <div className="rounded-2xl border bg-white p-6 shadow-sm">
                          <h3 className="mb-6 flex items-center gap-2 font-serif text-lg font-bold text-foreground">
                            <Utensils className="h-5 w-5 text-primary shrink-0" />
                            Sponsorship details
                          </h3>
                          <div className="grid gap-y-4 gap-x-8 text-xs sm:grid-cols-3">
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <Utensils className="h-3.5 w-3.5 text-primary" /> PACKAGE
                              </span>
                              <span className="font-semibold text-foreground mt-1 block ml-5 capitalize">
                                {safeStr(selectedDonation.bhojan_name || selectedDonation.donation_type || selectedDonation.category)}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <BookOpen className="h-3.5 w-3.5 text-primary" /> SCHEME
                              </span>
                              <span className="font-semibold text-foreground mt-1 block ml-5 capitalize">
                                {safeStr(selectedDonation.scheme_name || selectedDonation.category || selectedDonation.sub_category)}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <Calendar className="h-3.5 w-3.5 text-primary" /> PREFERRED DATE
                              </span>
                              <span className="font-semibold text-foreground mt-1 block ml-5">
                                {formatDisplayDate(selectedDonation.preferred_date || selectedDonation.donation_date)}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <Calendar className="h-3.5 w-3.5 text-primary" /> PREFERRED TITHI
                              </span>
                              <span className="font-semibold text-foreground mt-1 block ml-5">
                                {safeStr(selectedDonation.preferred_tithi)}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <CreditCard className="h-3.5 w-3.5 text-primary" /> MODE
                              </span>
                              <span className="font-semibold text-foreground mt-1 block ml-5 capitalize">
                                {safeStr(selectedDonation.payment_mode || selectedDonation.mode)}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <Award className="h-3.5 w-3.5 text-primary" /> OCCASION
                              </span>
                              <span className="font-semibold text-foreground mt-1 block ml-5">
                                {safeStr(selectedDonation.occasion)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Transaction history section if any logs exist */}
                        {/* Payment History section */}
                        {Array.isArray(selectedDonation.payment_history) && selectedDonation.payment_history.length > 0 && (
                          <div className="space-y-3">
                            <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-foreground">
                              <FileText className="h-5 w-5 text-primary shrink-0" />
                              Payment history
                            </h3>
                            <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-[#F8F6F2] text-xs font-semibold text-muted-foreground">
                                    <tr>
                                      <th className="p-4 text-left">Order ID</th>
                                      <th className="p-4 text-left">Payment ID</th>
                                      <th className="p-4 text-left">Method</th>
                                      <th className="p-4 text-left">Status</th>
                                      <th className="p-4 text-right">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y text-xs">
                                    {selectedDonation.payment_history.map((ph: any) => (
                                      <tr key={ph.id} className="hover:bg-muted/10">
                                        <td className="p-4 font-mono text-foreground">
                                          {safeStr(ph.razorpay_order_id)}
                                        </td>
                                        <td className="p-4 font-mono text-foreground">
                                          {safeStr(ph.razorpay_payment_id)}
                                        </td>
                                        <td className="p-4 font-semibold text-foreground capitalize">
                                          {safeStr(ph.payment_method)}
                                        </td>
                                        <td className="p-4">
                                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white capitalize ${ph.status === "success" ? "bg-emerald-600" : "bg-destructive"
                                            }`}>
                                            {safeStr(ph.status)}
                                          </span>
                                        </td>
                                        <td className="p-4 text-right font-bold text-foreground">
                                          {inr(Number(ph.amount ?? 0))}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Footer Action Buttons */}
                        <div className="flex justify-end gap-3 pt-4 border-t">
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setIsDetailsOpen(false)}
                            className="text-muted-foreground hover:text-foreground font-bold"
                          >
                            Close
                          </Button>
                          <Button
                            type="button"
                            onClick={() => downloadOne(selectedDonation)}
                            className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl flex items-center gap-2"
                          >
                            <DownloadIcon className="h-4 w-4 text-white" />
                            Download receipt
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>

              {/* Product Order Details Modal Dialog */}
              <Dialog open={isProductOrderDetailsOpen} onOpenChange={setIsProductOrderDetailsOpen}>
                <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-[#FBF9F6] p-6 sm:rounded-2xl border-none">
                  <DialogHeader className="sr-only">
                    <DialogTitle>Order Details</DialogTitle>
                  </DialogHeader>
                  {selectedProductOrder && (
                    <div className="space-y-6">
                      {/* Header Block */}
                      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
                        <div className="flex items-center gap-3">
                          <span className="bg-amber-100 grid h-10 w-10 place-items-center rounded-xl text-amber-700">
                            <ShoppingBag className="h-5 w-5" />
                          </span>
                          <div>
                            <h2 className="!font-sans !text-2xl font-bold text-foreground">
                              Order #{selectedProductOrder.id ? `JU-${selectedProductOrder.id}` : selectedProductOrder.order_number}
                            </h2>
                            <p className="text-xs text-muted-foreground">
                              Placed on {selectedProductOrder.created_at ? new Date(selectedProductOrder.created_at).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              }) : "—"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                              Order Status
                            </span>
                            <Badge className="capitalize font-bold px-3 py-1 bg-amber-500/10 text-amber-800 border-amber-300">
                              {selectedProductOrder.order_status || selectedProductOrder.status || "Pending"}
                            </Badge>
                          </div>
                          <div className="text-center">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                              Payment Status
                            </span>
                            <Badge className={`capitalize font-bold px-3 py-1 ${
                              ["paid", "success", "completed", "captured"].includes(
                                (selectedProductOrder.payment_status || "").toLowerCase().trim()
                              )
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : ["failed", "cancelled", "rejected"].includes(
                                    (selectedProductOrder.payment_status || "").toLowerCase().trim()
                                  )
                                  ? "bg-destructive text-white"
                                  : "bg-amber-600 text-white"
                            }`}>
                              {selectedProductOrder.payment_status || "Pending"}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Customer & Address Grid */}
                      <div className="grid gap-6 md:grid-cols-2">
                        {/* Customer Details */}
                        <div className="rounded-2xl border bg-white p-5 shadow-xs">
                          <h3 className="mb-4 flex items-center gap-2 font-serif text-base font-bold text-foreground">
                            <UserIcon className="h-4 w-4 text-primary shrink-0" />
                            Customer Information
                          </h3>
                          <div className="space-y-3 text-xs">
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Name</span>
                              <span className="font-semibold text-foreground mt-0.5 block">
                                {selectedProductOrder.user_name || selectedProductOrder.customer_name || "—"}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Phone Number</span>
                              <span className="font-semibold text-foreground mt-0.5 block">
                                {selectedProductOrder.user_phone_number || selectedProductOrder.customer_mobile || selectedProductOrder.mobile || "—"}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Payment Method</span>
                              <span className="font-semibold text-foreground mt-0.5 block uppercase">
                                {selectedProductOrder.payment_method === "cod" ? "Cash on Delivery (COD)" : selectedProductOrder.payment_method || "Online"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Delivery Address */}
                        <div className="rounded-2xl border bg-white p-5 shadow-xs">
                          <h3 className="mb-4 flex items-center gap-2 font-serif text-base font-bold text-foreground">
                            <MapPin className="h-4 w-4 text-primary shrink-0" />
                            Delivery Address
                          </h3>
                          <div className="space-y-2 text-xs">
                            <p className="font-semibold text-foreground">
                              {[
                                selectedProductOrder.user_details?.address_line_1 || selectedProductOrder.address,
                                selectedProductOrder.user_details?.address_line_2,
                                selectedProductOrder.user_details?.landmark
                              ].filter(Boolean).join(", ") || "—"}
                            </p>
                            <p className="text-muted-foreground">
                              {[
                                selectedProductOrder.user_details?.city,
                                selectedProductOrder.user_details?.state,
                                selectedProductOrder.user_details?.pincode
                              ].filter(Boolean).join(", ")}
                            </p>
                            {selectedProductOrder.user_details?.alternate_phone_number && (
                              <p className="text-muted-foreground">
                                Alt Phone: <span className="font-semibold text-foreground">{selectedProductOrder.user_details.alternate_phone_number}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Ordered Items Table */}
                      <div className="rounded-2xl border bg-white p-5 shadow-xs">
                        <h3 className="mb-4 flex items-center gap-2 font-serif text-base font-bold text-foreground">
                          <Package className="h-4 w-4 text-primary shrink-0" />
                          Order Items
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-[#F8F6F2] font-semibold text-muted-foreground border-b">
                              <tr>
                                <th className="p-3 text-center w-12">No.</th>
                                <th className="p-3 text-left">Item Name & Variant</th>
                                <th className="p-3 text-center">Qty</th>
                                <th className="p-3 text-right">Price</th>
                                <th className="p-3 text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {(() => {
                                let itemsList: ProductOrderItem[] = [];
                                if (Array.isArray(selectedProductOrder.items)) itemsList = selectedProductOrder.items;
                                else if (typeof selectedProductOrder.items === "string") {
                                  try { itemsList = JSON.parse(selectedProductOrder.items); } catch { }
                                }
                                if (!itemsList.length) {
                                  return (
                                    <tr>
                                      <td colSpan={5} className="p-4 text-center text-muted-foreground">No items listed</td>
                                    </tr>
                                  );
                                }
                                return itemsList.map((item, idx) => (
                                  <tr key={item.id || idx} className="hover:bg-muted/10">
                                    <td className="p-3 text-center font-medium text-muted-foreground">{idx + 1}</td>
                                    <td className="p-3 font-semibold text-foreground">
                                      {item.product_name || item.name || "Product"}
                                      {item.variant_value ? (
                                        <span className="ml-1 text-muted-foreground font-normal">({item.variant_value}g)</span>
                                      ) : null}
                                    </td>
                                    <td className="p-3 text-center font-bold">{item.quantity ?? 1}</td>
                                    <td className="p-3 text-right font-medium">{inr(Number(item.price ?? 0))}</td>
                                    <td className="p-3 text-right font-bold text-foreground">{inr(Number(item.total ?? 0))}</td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Pricing Summary */}
                      <div className="rounded-2xl border bg-white p-5 shadow-xs">
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between text-muted-foreground">
                            <span>Items Subtotal</span>
                            <span className="font-semibold text-foreground">{inr(Number(selectedProductOrder.subtotal ?? selectedProductOrder.total_amount ?? 0))}</span>
                          </div>
                          <div className="flex justify-between text-muted-foreground">
                            <span>Delivery Charge</span>
                            <span className="font-semibold text-foreground">{inr(Number(selectedProductOrder.delivery_charge ?? 0))}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2 text-sm font-bold text-foreground">
                            <span>Total Amount</span>
                            <span className="text-primary">{inr(Number(selectedProductOrder.total_amount ?? 0))}</span>
                          </div>
                        </div>
                      </div>

                      {/* Modal Action Buttons */}
                      <div className="flex justify-end gap-3 pt-2 border-t">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setIsProductOrderDetailsOpen(false)}
                          className="text-muted-foreground hover:text-foreground font-bold"
                        >
                          Close
                        </Button>
                        <Button
                          type="button"
                          onClick={() => downloadProductOrderReceiptPdf(selectedProductOrder)}
                          className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl flex items-center gap-2"
                        >
                          <DownloadIcon className="h-4 w-4 text-white" />
                          Download Receipt
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </Tabs>
          </section>
        </>
      )}
    </PageShell>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border bg-card p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="bg-primary/10 grid h-7 w-7 place-items-center rounded-lg text-primary">
          {icon}
        </span>
        <span className="text-xs font-medium uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p className="mt-2 text-2xl font-bold">{value}</p>
    </div>
  );
}

function ProductOrdersList({
  rows,
  loading,
  onViewDetails,
  onDownloadReceipt,
}: {
  rows: ProductOrder[];
  loading: boolean;
  onViewDetails: (order: ProductOrder) => void;
  onDownloadReceipt: (order: ProductOrder) => void;
}) {
  if (loading) {
    return (
      <div className="grid min-h-[30vh] place-items-center rounded-2xl border bg-card p-10">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-dashed p-10 text-center bg-card">
        <ShoppingBag className="text-muted-foreground/60 mx-auto h-12 w-12" />
        <h3 className="mt-4 text-lg font-semibold">No orders yet</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          You haven't placed any snack or product orders yet. Explore our Gruh Udhyog section!
        </p>
        <Button asChild className="mt-4" variant="outline">
          <Link to="/gruh-udhyog">Browse Products</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {rows.map((order) => {
        let itemsList: ProductOrderItem[] = [];
        if (Array.isArray(order.items)) itemsList = order.items;
        else if (typeof order.items === "string") {
          try { itemsList = JSON.parse(order.items); } catch { }
        }

        const addressParts = [
          order.user_details?.address_line_1 || order.address,
          order.user_details?.city,
          order.user_details?.state,
          order.user_details?.pincode,
        ].filter(Boolean);

        return (
          <div
            key={order.id}
            className="shadow-xs relative overflow-hidden rounded-2xl border bg-card p-6 transition-all hover:shadow-md"
          >
            <div className="absolute left-0 top-0 h-full w-2 bg-amber-600" />

            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center border-b pb-4">
              <div className="space-y-1 pl-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <h4 className="!font-sans !text-xl font-bold text-foreground">
                    Order #{order.id ? `JU-${order.id}` : order.order_number}
                  </h4>
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5 bg-muted/60 px-2.5 py-1 rounded-md font-medium">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    {order.created_at
                      ? new Date(order.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      : "—"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 shrink-0">
                <div className="text-center">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                    Order Status
                  </span>
                  <Badge className="capitalize font-bold px-3 py-1 bg-amber-500/10 text-amber-800 border-amber-300">
                    {order.order_status || order.status || "Pending"}
                  </Badge>
                </div>
                <div className="text-center">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                    Payment Status
                  </span>
                  <Badge
                    className={`capitalize font-bold px-3 py-1 ${
                      ["paid", "success", "completed", "captured"].includes(
                        (order.payment_status || "").toLowerCase().trim()
                      )
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : ["failed", "cancelled", "rejected"].includes(
                            (order.payment_status || "").toLowerCase().trim()
                          )
                          ? "bg-destructive text-white"
                          : "bg-amber-600 text-white"
                    }`}
                  >
                    {order.payment_status || "Pending"}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid gap-4 py-4 md:grid-cols-3 pl-2 text-xs">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">
                  Items ({itemsList.length})
                </span>
                <div className="mt-1 space-y-1">
                  {itemsList.slice(0, 2).map((item, idx) => (
                    <div key={idx} className="flex justify-between gap-2 text-foreground font-medium">
                      <span className="truncate">
                        • {item.product_name || item.name}{" "}
                        {item.variant_value ? `(${item.variant_value}g)` : ""}
                      </span>
                      <span className="shrink-0 font-semibold">x{item.quantity ?? 1}</span>
                    </div>
                  ))}
                  {itemsList.length > 2 && (
                    <p className="text-[11px] text-muted-foreground italic">+ {itemsList.length - 2} more item(s)</p>
                  )}
                </div>
              </div>

              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">
                  Delivery Address
                </span>
                <p className="mt-1 font-medium text-foreground line-clamp-2">
                  {addressParts.join(", ") || "—"}
                </p>
                <p className="mt-0.5 text-muted-foreground">
                  Phone: <span className="font-semibold text-foreground">{order.user_phone_number || order.customer_mobile || "—"}</span>
                </p>
              </div>

              <div className="flex flex-col justify-between items-start md:items-end">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block md:text-right">
                    Total Amount
                  </span>
                  <span className="text-xl font-bold text-primary block mt-0.5">
                    {inr(Number(order.total_amount ?? 0))}
                  </span>
                  <span className="text-[10px] text-muted-foreground uppercase block md:text-right">
                    Method: {order.payment_method === "cod" ? "COD" : order.payment_method || "Online"}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onViewDetails(order)}
                    className="h-8 text-xs font-bold"
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" />
                    View Details
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onDownloadReceipt(order)}
                    className="h-8 text-xs font-bold bg-primary text-white hover:bg-primary/95"
                  >
                    <DownloadIcon className="mr-1 h-3.5 w-3.5 text-white" />
                    Receipt
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonationsTable({
  rows,
  onDownload,
  onViewDetails,
}: {
  rows: any[];
  onDownload: (d: any) => void;
  onViewDetails: (d: any) => void;
}) {
  const { t } = useI18n();
  if (!rows.length) {
    return (
      <div className="rounded-2xl border bg-card p-10 text-center">
        <p className="text-muted-foreground">
          {t("account.account.donationsTable.empty")}
        </p>
        <Button asChild className="mt-4">
          <Link to="/donate">
            {t("account.account.donationsTable.makeFirst")}
          </Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.date")}
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.receipt")}
              </th>
              <th className="p-3 text-left">
                Type
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.cause")}
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.mode")}
              </th>
              <th className="p-3 text-right">
                {t("account.account.donationsTable.amount")}
              </th>
              <th className="p-3 text-left">
                {t("account.account.donationsTable.status")}
              </th>
              <th className="p-3 text-right">
                {t("account.account.donationsTable.receiptAction")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(d => (
              <tr key={d.id} className="hover:bg-muted/20 border-t">
                <td className="whitespace-nowrap p-3">
                  {formatDisplayDate(d.donation_date || d.created_at)}
                </td>
                <td className="p-3 font-mono text-xs">{d.receipt_no}</td>
                <td className="p-3 capitalize">{d.type ?? "—"}</td>
                <td className="p-3">{d.purpose ?? d.category ?? "—"}</td>
                <td className="p-3 capitalize">{d.payment_mode ?? "—"}</td>
                <td className="p-3 text-right font-semibold">
                  {inr(d.amount)}
                </td>
                <td className="p-3">
                  <Badge
                    variant={
                      d.payment_status === "success"
                        ? "default"
                        : d.payment_status === "pending"
                          ? "secondary"
                          : "destructive"
                    }
                  >
                    {d.payment_status}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewDetails(d)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    {d.payment_status === "success" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDownload(d)}
                      >
                        <DownloadIcon className="mr-1 h-3.5 w-3.5" />
                        {t("account.account.donationsTable.download")}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground min-w-[80px] text-center">
                        {t("account.account.donationsTable.pending")}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function ProfileForm({
  donor,
  userDetails,
  onSave,
  onCancel,
}: {
  donor: any;
  userDetails: any;
  onSave: (
    donorPayload: any,
    userDetailsPayload: any,
    personalPayload: any
  ) => void | Promise<void>;
  onCancel?: () => void;
}) {
  const { t } = useI18n();
  const { user: authUser } = useAuth();

  const [personal, setPersonal] = useState({
    first_name: authUser?.first_name ?? "",
    last_name: authUser?.last_name ?? "",
    full_name: authUser?.full_name ?? "",
    email: authUser?.email ?? "",
    phone_number: authUser?.phone_number ?? "",
    gender: authUser?.gender ?? "",
    date_of_birth: authUser?.date_of_birth ?? "",
  });

  const [f, setF] = useState({
    full_name: donor?.full_name ?? "",
    mobile: donor?.mobile ?? "",
    email: donor?.email ?? "",
    pan: donor?.pan ?? "",
    address: donor?.address ?? "",
    city: donor?.city ?? "",
    state: donor?.state ?? "",
    pincode: donor?.pincode ?? "",
    country: donor?.country ?? "India",
    date_of_birth: donor?.date_of_birth ?? "",
  });

  const [u, setU] = useState({
    id: userDetails?.id,
    address_line_1: userDetails?.address_line_1 ?? "",
    address_line_2: userDetails?.address_line_2 ?? "",
    landmark: userDetails?.landmark ?? "",
    city: userDetails?.city ?? "",
    state: userDetails?.state ?? "",
    pincode: userDetails?.pincode ?? "",
    alternate_phone_number: userDetails?.alternate_phone_number ?? "",
  });

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authUser) {
      setPersonal({
        first_name: authUser.first_name ?? "",
        last_name: authUser.last_name ?? "",
        full_name: authUser.full_name ?? "",
        email: authUser.email ?? "",
        phone_number: authUser.phone_number ?? "",
        gender: authUser.gender ?? "",
        date_of_birth: authUser.date_of_birth ?? "",
      });
    }
  }, [authUser]);

  useEffect(() => {
    if (donor) {
      setF({
        full_name: donor.full_name ?? "",
        mobile: donor.mobile ?? "",
        email: donor.email ?? "",
        pan: donor.pan ?? "",
        address: donor.address ?? "",
        city: donor.city ?? "",
        state: donor.state ?? "",
        pincode: donor.pincode ?? "",
        country: donor.country ?? "India",
        date_of_birth: donor.date_of_birth ?? "",
      });
    }
  }, [donor]);

  useEffect(() => {
    if (userDetails) {
      setU({
        id: userDetails.id,
        address_line_1: userDetails.address_line_1 ?? "",
        address_line_2: userDetails.address_line_2 ?? "",
        landmark: userDetails.landmark ?? "",
        city: userDetails.city ?? "",
        state: userDetails.state ?? "",
        pincode: userDetails.pincode ?? "",
        alternate_phone_number: userDetails.alternate_phone_number ?? "",
      });
    }
  }, [userDetails]);

  return (
    <form
      className="space-y-6"
      onSubmit={async e => {
        e.preventDefault();
        setBusy(true);
        try {
          await onSave(f, u, personal);
        } finally {
          setBusy(false);
        }
      }}
    >
      {/* Personal Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-primary">
          Personal Information
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Update your personal details including name, phone, date of birth and
          gender.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="First Name *">
            <Input
              required
              value={personal.first_name}
              onChange={e =>
                setPersonal({ ...personal, first_name: e.target.value })
              }
              placeholder="First Name"
            />
          </Field>
          <Field label="Last Name">
            <Input
              value={personal.last_name}
              onChange={e =>
                setPersonal({ ...personal, last_name: e.target.value })
              }
              placeholder="Last Name"
            />
          </Field>
          <Field label="Full Name">
            <Input
              value={personal.full_name}
              onChange={e =>
                setPersonal({ ...personal, full_name: e.target.value })
              }
              placeholder="Full Name"
            />
          </Field>
          <Field label="Mobile Number">
            <Input
              value={personal.phone_number}
              onChange={e =>
                setPersonal({ ...personal, phone_number: e.target.value })
              }
              placeholder="Mobile Number"
            />
          </Field>
          <Field label="Email Address">
            <Input
              type="email"
              value={personal.email}
              disabled
              className="bg-muted opacity-70"
            />
          </Field>
          <Field label="Gender">
            <select
              value={personal.gender}
              onChange={e =>
                setPersonal({ ...personal, gender: e.target.value })
              }
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          <Field label="Date of Birth">
            <Input
              type="date"
              value={personal.date_of_birth}
              onChange={e =>
                setPersonal({ ...personal, date_of_birth: e.target.value })
              }
            />
          </Field>
          <Field label="Username">
            <Input
              value={authUser?.username ?? ""}
              disabled
              className="bg-muted opacity-70"
            />
          </Field>
        </div>
      </div>

      <div className="my-6 border-t border-border" />

      {donor ? (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-primary">
            {t("account.account.profile.title")}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("account.account.profile.description")}
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label={t("account.account.profile.fullName")}>
              <Input
                required
                value={f.full_name}
                onChange={e => setF({ ...f, full_name: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.mobile")}>
              <Input
                required
                value={f.mobile}
                onChange={e => setF({ ...f, mobile: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.email")}>
              <Input
                type="email"
                value={f.email}
                onChange={e => setF({ ...f, email: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.pan")}>
              <Input
                value={f.pan}
                onChange={e =>
                  setF({ ...f, pan: e.target.value.toUpperCase() })
                }
                placeholder={t("account.account.profile.panPlaceholder")}
              />
            </Field>
            <Field label={t("account.account.profile.dateOfBirth")}>
              <Input
                type="date"
                value={f.date_of_birth ?? ""}
                onChange={e => setF({ ...f, date_of_birth: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.country")}>
              <Input
                value={f.country}
                onChange={e => setF({ ...f, country: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.address")} full>
              <Textarea
                rows={2}
                value={f.address}
                onChange={e => setF({ ...f, address: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.city")}>
              <Input
                value={f.city}
                onChange={e => setF({ ...f, city: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.state")}>
              <Input
                value={f.state}
                onChange={e => setF({ ...f, state: e.target.value })}
              />
            </Field>
            <Field label={t("account.account.profile.pincode")}>
              <Input
                value={f.pincode}
                onChange={e => setF({ ...f, pincode: e.target.value })}
              />
            </Field>
          </div>
          <div className="my-6 border-t border-border" />
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-700">
          <p className="font-bold">No Donor Profile Linked Yet</p>
          <p className="mt-1">
            Make your first donation and a tax receipt donor profile will be
            automatically linked to your account.
          </p>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-primary">
          User Profile Details
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Update your personal contact numbers and primary residence address
          details.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Address Line 1">
            <Input
              value={u.address_line_1}
              onChange={e => setU({ ...u, address_line_1: e.target.value })}
              placeholder="House, apartment, building number"
            />
          </Field>
          <Field label="Address Line 2">
            <Input
              value={u.address_line_2}
              onChange={e => setU({ ...u, address_line_2: e.target.value })}
              placeholder="Street, area name"
            />
          </Field>
          <Field label="Landmark">
            <Input
              value={u.landmark}
              onChange={e => setU({ ...u, landmark: e.target.value })}
              placeholder="e.g. Near Jain Temple"
            />
          </Field>
          <Field label="Alternate Phone Number">
            <Input
              value={u.alternate_phone_number}
              onChange={e =>
                setU({ ...u, alternate_phone_number: e.target.value })
              }
              placeholder="Alternate mobile or landline number"
            />
          </Field>
          <Field label="City">
            <Input
              value={u.city}
              onChange={e => setU({ ...u, city: e.target.value })}
              placeholder="City"
            />
          </Field>
          <Field label="State">
            <Input
              value={u.state}
              onChange={e => setU({ ...u, state: e.target.value })}
              placeholder="State"
            />
          </Field>
          <Field label="Pincode">
            <Input
              value={u.pincode}
              onChange={e => setU({ ...u, pincode: e.target.value })}
              placeholder="Pincode"
            />
          </Field>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3 border-t pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {t("account.account.profile.save")}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
